import { useCallback } from 'react';
import useIndexedDB from './useIndexedDB';

const getQualificationKey = (questionId) => `qualification:${questionId}`;

const addMonths = (date, months) => {
  const result = new Date(date);
  const originalDay = result.getUTCDate();

  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + months);

  const lastDayOfTargetMonth = new Date(
    Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0),
  ).getUTCDate();

  result.setUTCDate(Math.min(originalDay, lastDayOfTargetMonth));

  return result;
};

const isFresh = (answeredAt, maxAgeMonths) => {
  if (!answeredAt) {
    return false;
  }

  const answeredDate = new Date(answeredAt);

  if (Number.isNaN(answeredDate.getTime())) {
    return false;
  }

  const expiryDate = addMonths(answeredDate, maxAgeMonths);

  return Date.now() < expiryDate.getTime();
};

const useQualificationCache = ({ maxAgeMonths = 3 } = {}) => {
  const { getData, setData, removeData } = useIndexedDB();

  const getQualification = useCallback(
    async (questionId) => {
      if (!questionId) {
        return null;
      }

      const data = await getData(getQualificationKey(questionId));

      if (!data) {
        return null;
      }

      if (!isFresh(data.answeredAt, maxAgeMonths)) {
        return null;
      }

      return data;
    },
    [getData, maxAgeMonths],
  );

  const saveQualification = useCallback(
    async (questionId, response) => {
      if (!questionId) {
        return;
      }

      return setData(getQualificationKey(questionId), {
        questionId,
        questionName: response?.questionName ?? null,
        user_response: response?.user_response,
        answeredAt: new Date().toISOString(),
      });
    },
    [setData],
  );

  const removeQualification = useCallback(
    async (questionId) => {
      if (!questionId) {
        return;
      }
      return removeData(getQualificationKey(questionId));
    },
    [removeData],
  );

  return {
    getQualification,
    saveQualification,
    removeQualification,
  };
};

export default useQualificationCache;
